import React, { useEffect, useState } from "react";

import { Box } from "@chakra-ui/react";
import { getBusinessYield } from "api/business-user-management";
import {
  IBusinessYieldData,
  TYieldDisplayByType,
} from "api/business-user-management/types";
import { useParams, useSearchParams } from "react-router-dom";
import {
  getTodayAsEndDateFilterValue,
  handleAmountFormat,
  handleTruncateAmount,
} from "utils";
import { ONE_NAIRA_IN_KOBO, ZERO } from "utils/constants";
import { Maybe } from "utils/general/maybe";

import { customErrorToast } from "ui/custom-toast";
import { DEFAULT_PAGINATION_DATA } from "ui/pagination";
import { Spinner } from "ui/spinner";
import { Status } from "ui/status";
import { Heading3, Paragraph3 } from "ui/typography";

import { BusinessYieldTable } from "./BusinessYieldTable";
import { DEFAULT_YIELD_PAGINATION_PARAMS } from "./const";
import { IGetBusinessYieldPayload } from "./types";

export const BusinessYield = () => {
  const { businessId } = useParams();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");
  const limit = searchParams.get("limit");
  const displayByType = searchParams.get("displayByType");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const filterRetryCount = searchParams.get("filterRetryCount");
  const [isLoadingYield, setIsLoadingYield] = useState(false);
  const [businessYieldData, setBusinessYieldData] =
    useState<IBusinessYieldData>({
      yieldStatus: false,
      totalInterestEarned: ZERO,
      data: [],
      ...DEFAULT_PAGINATION_DATA,
    });
  const {
    yieldStatus,
    totalInterestEarned,
    data: businessYields,
    ...paginationData
  } = businessYieldData;

  const [yieldEarnedNaira, yieldEarnedKobo] = handleAmountFormat({
    value: String(
      handleTruncateAmount(totalInterestEarned) / ONE_NAIRA_IN_KOBO
    ),
    includeNairaSymbol: true,
    shouldFormatZero: true,
  }).split(".");

  const handleGetBusinessYield = async (payload: IGetBusinessYieldPayload) => {
    const { isRefetching, ...yieldPayload } = payload;
    if (!isRefetching) {
      setIsLoadingYield(true);
    }
    try {
      const { error, data, message } = await getBusinessYield(yieldPayload);
      if (error) {
        customErrorToast({ message });
      } else {
        setBusinessYieldData(data);
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    if (!isRefetching) {
      setIsLoadingYield(false);
    }
  };

  useEffect(() => {
    if (!businessId) return;
    const type = (displayByType as TYieldDisplayByType) || "days";
    const payload: IGetBusinessYieldPayload = {
      displayByType: type,
      businessId,
      page: Number(page || DEFAULT_YIELD_PAGINATION_PARAMS.page),
      limit: Number(limit || DEFAULT_YIELD_PAGINATION_PARAMS.limit),
      isRefetching: false,
    };

    if (startDate) {
      payload["startDate"] = startDate;
    }
    payload["endDate"] = endDate || getTodayAsEndDateFilterValue();
    void handleGetBusinessYield(payload);
  }, [
    businessId,
    page,
    limit,
    displayByType,
    startDate,
    endDate,
    filterRetryCount,
  ]);

  return (
    <Box className="flex flex-col gap-2 mt-1">
      <Box
        className="bg-white pt-5 px-6 pb-4 rounded-[20px] flex items-center"
        css={{ boxShadow: "0px 0px 5px 0px #0000000D" }}
      >
        <Maybe condition={isLoadingYield}>
          <Spinner size="30px" />
        </Maybe>
        <Maybe condition={!isLoadingYield}>
          <Box className="flex flex-col gap-1 w-full max-w-[273px]">
            <Paragraph3
              lineHeight="14px"
              textColor="#718096"
            >
              Total Yield Earned
            </Paragraph3>
            <Heading3
              lineHeight="20px"
              fontWeight="700"
              textColor="#1A202C"
            >
              {yieldEarnedNaira}.
              <span className="text-sm font-[700]">
                {yieldEarnedKobo || "00"}
              </span>
            </Heading3>
          </Box>
          <Box className="flex flex-col gap-1">
            <Paragraph3
              lineHeight="14px"
              textColor="#718096"
            >
              Yield Status
            </Paragraph3>
            <Status
              status={yieldStatus ? "success" : "disabled"}
              text={yieldStatus ? "Opted In" : "Not Opted In"}
              className="w-fit"
            />
          </Box>
        </Maybe>
      </Box>
      <BusinessYieldTable
        handleGetBusinessYield={handleGetBusinessYield}
        isLoadingYield={isLoadingYield}
        businessYields={businessYields}
        paginationData={paginationData}
        displayByType={displayByType}
      />
    </Box>
  );
};
